import React from "react";
// ANT DESIGN COMPONENTS
import { Tag, Tooltip } from "antd";
// CONFIG
import { ACTION_TYPE_CONFIG } from "../../monitoringManagement/Actions/actionTypeConfig";

/**
 * Tag colour per button type.
 *
 * A `Record` over `ACTION_TYPE_CONFIG`'s keys, so the set this can render is
 * the set that exists: adding a button type without giving it a colour fails to
 * compile rather than rendering nothing.
 *
 * That is the bug this replaces. The previous version was a chain of six
 * `actionType === "X" && <Tag/>` expressions with no else, so ON_OFF, SINGLE
 * and ONE_SHOT produced an EMPTY CELL — not "unknown", not a fallback, just
 * nothing. It stayed invisible until those values started arriving: while the
 * FlatBuffer enum stopped at SWITCH the resolver fell back to SWITCH, so the
 * column showed a plausible wrong answer, and the moment the enum was corrected
 * it showed no answer at all.
 */
const TYPE_COLOR: Record<keyof typeof ACTION_TYPE_CONFIG, string> = {
  TOGGLE: "blue",
  TOGGLETIMER: "orange",
  HOLDBACK: "green",
  TURNBACK: "purple",
  ENERGIZER_TURN_BACK: "volcano",
  SWITCH: "magenta",
  ON_OFF: "cyan",
  SINGLE: "geekblue",
  ONE_SHOT: "gold",
};

/** Shortened labels, where the full enum name does not fit a table cell. */
const TYPE_LABEL: Partial<Record<keyof typeof ACTION_TYPE_CONFIG, string>> = {
  ENERGIZER_TURN_BACK: "ENERGIZE",
};

function isKnownType(value: string): value is keyof typeof ACTION_TYPE_CONFIG {
  return value in ACTION_TYPE_CONFIG;
}

/**
 * The hover explanation for one button type, assembled from the SAME guide copy
 * the configuration screens show.
 *
 * Reusing `ACTION_TYPE_CONFIG.guide` rather than writing a second description
 * is the point: an operator reading a table cell and an operator reading the
 * setup guide were otherwise reading two independent accounts of one
 * interaction, and nothing kept them in step. A tag that says ONE_SHOT is
 * meaningless on its own — the whole reason it is not ON_OFF is a behavioural
 * difference the tag cannot show.
 */
function describeType(type: keyof typeof ACTION_TYPE_CONFIG): string {
  const { label, guide } = ACTION_TYPE_CONFIG[type];
  const bullets = (guide.bullets ?? []).map((b) => `• ${b.text}`).join(" ");
  return [`${label} — ${guide.intro.text}`, bullets, guide.outro?.text].filter(Boolean).join(" ");
}

function TableColumnBankActionType({ actionType }: { actionType: string }) {
  if (!actionType) {
    return (
      <div>
        <Tooltip title="This row has no button type set, so the monitoring panel cannot decide which controls to render for it.">
          <Tag color="red" className="drawer__typeTag">
            N/A
          </Tag>
        </Tooltip>
      </div>
    );
  }

  // An unrecognised type is shown, not swallowed. A row whose type this build
  // does not know about is a real condition — a server ahead of the client —
  // and the operator needs to see the cell is unresolved rather than read an
  // empty cell as "no type set".
  if (!isKnownType(actionType)) {
    return (
      <div>
        <Tooltip
          title={`"${actionType}" is not a button type this build knows about — the server is ahead of this client, or the value was written by hand.`}
        >
          <Tag color="red" className="drawer__typeTag">
            {actionType}
          </Tag>
        </Tooltip>
      </div>
    );
  }

  return (
    <div>
      <Tooltip title={describeType(actionType)}>
        <Tag color={TYPE_COLOR[actionType]} className="drawer__typeTag">
          {TYPE_LABEL[actionType] ?? actionType}
        </Tag>
      </Tooltip>
    </div>
  );
}

export default TableColumnBankActionType;
